import React, { useState,  useRef, useImperativeHandle, forwardRef } from "react";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { LinearProgress } from '@mui/material'
import {generateBlob} from 'react-qrcode-logo'
const JSZip = require('jszip')


const allowedExt = [".xlsx"];
const _notExcelFileWarning = "Chosen file is not an Excel file (.xlsx)!";
const _FPSSheetNotInWB = "Cannot find the work sheet `FPS QR Code`";
const _targetSheetName = "FPS QR Code";

const _headerTranslate = {
  BillType: "Bill Type",
  Amount: "Transaction",
  RefLabelValue1: "Partner ID",
  RefLabelValue2: "Designation",
  RefLabelValue3: "Motivation",
};

/** ExcelJS cell value → plain string */
const cellToString = (value) => {
  if (value == null || value === "") return "";
  if (typeof value === "object") {
    if (value.text != null) return String(value.text);
    if (value.result != null) return String(value.result);
    if (Array.isArray(value.richText)) {
      return value.richText.map((t) => t.text).join("");
    }
  }
  return String(value);
};

const loadWorkbook = async (file) => {
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(await file.arrayBuffer());
  return workbook;
};

/** Header row (row 1) → array of header strings (1-based cols) */
const readHeaderRow = (sheet) => {
  const headers = [];
  const row = sheet.getRow(1);
  row.eachCell({ includeEmpty: false }, (cell, colNumber) => {
    headers[colNumber] = cellToString(cell.value);
  });
  return headers;
};

/** Sheet → array of row objects keyed by header text */
const sheetToJson = (sheet) => {
  const headers = readHeaderRow(sheet);
  const rows = [];
  const maxRow = sheet.actualRowCount || sheet.rowCount;

  for (let r = 2; r <= maxRow; r++) {
    const row = sheet.getRow(r);
    const obj = {};
    let hasValue = false;
    headers.forEach((header, colNumber) => {
      if (!header) return;
      const raw = row.getCell(colNumber).value;
      const text = cellToString(raw);
      if (text !== "") hasValue = true;
      obj[header] = raw != null && typeof raw === "number" ? raw : text;
    });
    if (hasValue) rows.push(obj);
  }
  return rows;
};

/**
 * convert data to a format expected by the backend before sending 
 * @param { "parsed excel data to be sent to the server "} data 
 * @returns 
 */
const dataMassage = (data) => {
  let nuData = [];
  data.forEach((dataRow) => {
    let nuDataRow = {};
    Object.entries(_headerTranslate).forEach((pair) => {
      Object.keys(dataRow).forEach((oldK) => {
        if (oldK.substring(0, 15).includes(pair[1])) {
          nuDataRow[pair[0]] = dataRow[oldK];
        }
      });
    });
    nuData.push(nuDataRow);
  });

  nuData.forEach((r) => {
    if (!Object.keys(r).includes("Amount")) {
      r["Amount"] = 0;
    }
    r['Amount'] = r['Amount'] == '' ? 0 : r['Amount'];
  });

  const refLabels = Object.keys(_headerTranslate).filter((k) =>
    k.toLowerCase().includes("reflabel")
  );

  nuData.forEach((r) => {
    Object.entries(refLabels).forEach((k) => {
      let _k = k[1];
      r[_k] = Object.keys(r).includes(_k) ? r[_k].toString() : "";
    });
  });
  return nuData;
};

/**
 * Write API results into existing cells only (preserves sheet styles).
 * Column index is 1-based (ExcelJS).
 */
const writeDataToWorkSheet = (sheet, data) => {
  const ColDataIdx = [
    { cidx: 1, dataIdx: "BillType" },
    { cidx: 2, dataIdx: "Amount" },
    { cidx: 3, dataIdx: "RefLabelValue1" },
    { cidx: 4, dataIdx: "RefLabelValue2" },
    { cidx: 5, dataIdx: "RefLabelValue3" },
    { cidx: 6, dataIdx: "error" },
    { cidx: 7, dataIdx: "BillNumberReferenceLabel" },
    { cidx: 8, dataIdx: "code" },
    { cidx: 9, dataIdx: "filename" },
  ];

  for (let rIdx = 0; rIdx < data.length; rIdx++) {
    const row = sheet.getRow(rIdx + 2);
    ColDataIdx.forEach((cdi) => {
      const val = data[rIdx][cdi.dataIdx];
      // Set value only — keep existing cell font/fill/border
      row.getCell(cdi.cidx).value = val == null ? "" : val;
    });
    row.commit?.();
  }
};

/**
 * Cursor generated code to write trigger saving excel to client machine
 * Not used since the workbook is supposed to be a part of the qr code package 
 * @param {*} workbook 
 * @param {*} filename 
 */
const downloadWorkbook = async (workbook, filename) => {
  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(
    new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
    filename
  );
};


/**
 * Generate a zip file package including the workbook and related qr codes
 * qr codes are to be generated within this function
 * @param { "the workbook to be packaged"} wb 
 * @param { "the workbook name within the package (only use data where code is generated by the server ) " } wbName 
 * @param { "data return from the server" } data 
 * @param { "callback that takes in the percentage of total qr generated " } qrProgressSetter 
 */
const prepareZipFileAsync  = async (wb , wbName , data=undefined, qrProgressSetter = undefined) => {
    
    var zip = new JSZip();

    const wbBuffer = await wb.xlsx.writeBuffer();
    const wbBlob = new Blob([wbBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    zip.file( wbName , wbBlob  );

    // add qr code as blobs and insert them into zip object 
    if (data) {
        for (let dataIdx = 0; dataIdx < data.length; dataIdx++) {
            const elem = data[dataIdx];
            let qrBlob = await generateBlob({
                value: elem['code']
            ,   size : 200 
            });
            zip.file( elem['filename'] , qrBlob)

            // updates callback function 
            if (qrProgressSetter ){
                qrProgressSetter( 100* dataIdx/data.length );
            }
        }
    }


    zip.generateAsync({type:"blob"})
        .then((content)=>{
            saveAs(content , `BatchPackage_${Date.now()}.zip`);
        });
};


const CustomInput = forwardRef((props, ref) => {
    const [status, setStatus] = useState('')
    const [qrGenProgress, setQrGenProgress] = useState(0)

    const fileInputRef = useRef(null);

    useImperativeHandle(ref, () => ({
        click: () => {
        fileInputRef.current?.click();
        },
    }));

    // const updateStatus = (statusMsg) => {
    //     setStatus(statusMsg);
    // }

     
    /**
     * this event is triggered when the user selects a files and clicks ok 
     * parse the excel with expected worksheet and column name
     * send the json to the backend 
     * fill the excel spreadsheet with new data
     * generate the qr codes 
     * @param {*} e 
     * @returns 
     */
    const handleFileChange = async (e) => {
        const fileChosen = e.target.files[0];
        if (!fileChosen) return;

        
        const isExcelName = allowedExt.some((ext) =>
            fileChosen.name.toLowerCase().endsWith(ext)
        );

        try {
            if (!isExcelName) {
                throw new Error(_notExcelFileWarning);
            }

            // Locks the Generate QR Code button ( in the parent container) before proceeding with parsing and qr generation 
            props.ToggleGenButtonState(true) 

            setStatus("Reading Excel workbook")
            const workbook = await loadWorkbook(fileChosen);
            const sheet = workbook.getWorksheet(_targetSheetName);
            if (!sheet) {
                throw new Error(_FPSSheetNotInWB);
            }

            let data = sheetToJson(sheet);
            data = dataMassage(data);

            setStatus("Evaluating parameters")
            let serverData = await props.excelDataCallback(data);
            if (!serverData || !Array.isArray(serverData)) {
                throw new Error("No data returned from server");
            }

            console.log(`data return from api: ${Date.now()}`);

            // write data to the excel workbook
            setStatus("Writing new data to Excel")
            writeDataToWorkSheet(sheet, serverData);

            // generate qr codes and package them along with the workbook in a zip file  
            setStatus('Generating QR Code')
            await prepareZipFileAsync(   workbook 
                , `FPS_QR_Batch_Result_${Date.now()}.xlsx`
                , serverData.filter( entry=> entry['code'] != '')
                // setstate pass in as a callback func to update qr generation progress 
                , setQrGenProgress  
                        )

            // give one final status before hiding everything 
            setStatus('Completed')
            
            //  cursor generated code, which only returns the excel workbook
            //   await downloadWorkbook(
            //     workbook,
            //     `FPS_QR_Batch_Result_${Date.now()}.xlsx`
            //   );
        } catch (error) {
        alert(error.message || String(error));
        } finally {
          setTimeout(()=>{
              setStatus('')
              setQrGenProgress(0)
          } , 1500)
          e.target.value = "";
          props.ToggleGenButtonState(false)

        }
    };

  return (
    <div style={{margin: '0 10px'}} >
        <p>{status}</p>
        <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept={allowedExt.join(",")}
        style={{ display: "none" }}
        />
        <div   style={{visibility: qrGenProgress> 0? 'visible': 'hidden' }}  >
            <LinearProgress 
                variant="determinate"
                value={qrGenProgress}
                min={0}
                max={100}
            />
        </div>
    </div>
  );
});

export default CustomInput;
